import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { toPaymentMethodHintTranslationKey } from "../../../shared/staff.paymentMethod.logic";
import type {
  PaymentMethodDetailsForm,
  SelectOption,
} from "../../profilePanel.contract";
import {
  SalaryModalAddAction,
  SalaryModalError,
  SalaryModalFormGrid,
} from "../salary/profilePanel.salaryModal.styled";
import { PaymentMethodValueField } from "./profilePanel.paymentMethodModal.styled";

interface ProfilePanelPaymentMethodModalProps {
  canSavePaymentMethod: boolean;
  hasPaymentMethod: boolean;
  isOpen: boolean;
  isSaving: boolean;
  paymentMethodDetails: PaymentMethodDetailsForm;
  paymentMethodError: string;
  paymentMethodOptions: SelectOption[];
  saveError: string;
  onClose: () => void;
  onConfirm: () => void;
  onDelete: () => void;
  onPaymentMethodFieldChange: <TField extends keyof PaymentMethodDetailsForm>(
    field: TField,
    value: PaymentMethodDetailsForm[TField],
  ) => void;
}

const toPaymentMethodTypeOrDefault = (
  type: string,
): PaymentMethodDetailsForm["type"] => {
  if (type === "InternationalBankAccount") return "InternationalBankAccount";
  return "BankAccount";
};

export const ProfilePanelPaymentMethodModal = (
  props: ProfilePanelPaymentMethodModalProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const modalError =
    props.saveError.length > 0 && props.saveError !== props.paymentMethodError
      ? props.saveError
      : "";

  useEffect(() => {
    if (!props.isOpen) {
      setIsDeleteConfirmOpen(false);
    }
  }, [props.isOpen]);

  const handleDeleteClick = (): void => {
    setIsDeleteConfirmOpen(true);
  };

  const handleCancelDelete = (): void => {
    if (props.isSaving) return;
    setIsDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = (): void => {
    props.onDelete();
  };

  const handleEditModalClose = (): void => {
    if (isDeleteConfirmOpen) {
      handleCancelDelete();
      return;
    }
    props.onClose();
  };

  return (
    <>
      <Modal
        isOpen={props.isOpen}
        onClose={handleEditModalClose}
        label={t("Staff.ProfilePanelChangePaymentMethod")}
        size="compact"
        showCloseButton={false}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              disabled={props.isSaving}
              onClick={props.onClose}
            />
            <PrimaryButton
              label={t("General.Save")}
              isLoading={props.isSaving}
              disabled={props.isSaving || !props.canSavePaymentMethod}
              onClick={props.onConfirm}
            />
          </>
        }
      >
        <SalaryModalFormGrid>
          <Dropdown
            fullWidth
            label={t("Staff.PaymentMethodType")}
            options={props.paymentMethodOptions}
            value={props.paymentMethodDetails.type}
            onChange={(type) =>
              props.onPaymentMethodFieldChange(
                "type",
                toPaymentMethodTypeOrDefault(type),
              )
            }
          />
          <PaymentMethodValueField>
            <InputField
              fullWidth
              label={t("Staff.PaymentMethodValue")}
              value={props.paymentMethodDetails.value}
              hasError={props.paymentMethodError.length > 0}
              hintText={t(
                toPaymentMethodHintTranslationKey(
                  props.paymentMethodDetails.type,
                ),
              )}
              errorText={props.paymentMethodError}
              onClear={() => props.onPaymentMethodFieldChange("value", "")}
              onChange={(value) =>
                props.onPaymentMethodFieldChange("value", value)
              }
            />
          </PaymentMethodValueField>
          {props.hasPaymentMethod && (
            <SalaryModalAddAction>
              <SecondaryButton
                size="small"
                label={t("General.Remove")}
                disabled={props.isSaving}
                onClick={handleDeleteClick}
              />
            </SalaryModalAddAction>
          )}
        </SalaryModalFormGrid>

        {modalError.length > 0 && (
          <SalaryModalError>{modalError}</SalaryModalError>
        )}
      </Modal>
      <Modal
        isOpen={props.isOpen && isDeleteConfirmOpen}
        onClose={handleCancelDelete}
        label={t("Staff.PaymentMethodDeleteConfirmTitle")}
        size="compact"
        variant="confirm"
        showCloseButton={false}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              disabled={props.isSaving}
              onClick={handleCancelDelete}
            />
            <PrimaryButton
              label={t("General.Remove")}
              isLoading={props.isSaving}
              disabled={props.isSaving}
              onClick={handleConfirmDelete}
            />
          </>
        }
      >
        {t("Staff.PaymentMethodDeleteConfirmBody")}
      </Modal>
    </>
  );
};
